import { FrameFlag, type ProtocolMessage } from "./protocol";

const CLIENT_PROOF_DOMAIN = "E9-AUTH-CLIENT-V2";
const SESSION_KEY_DOMAIN = "E9-SESSION-V2";
const SERVER_PROOF_DOMAIN = "E9-AUTH-SERVER-V2";
const MESSAGE_DOMAIN = "E9-MSG-V2";
const ACCESS_KEY_BYTES = 16;
const AUTH_TAG_BYTES = 16;
const AUTH_TRAILER_BYTES = 20;
const STORAGE_PREFIX = "espress9th.access.";
const textEncoder = new TextEncoder();

function concat(...values: readonly Uint8Array[]): Uint8Array {
  const output = new Uint8Array(values.reduce((size, value) => size + value.length, 0));
  let offset = 0;
  for (const value of values) {
    output.set(value, offset);
    offset += value.length;
  }
  return output;
}

function ascii(value: string): Uint8Array {
  return textEncoder.encode(value);
}

async function hmac(key: Uint8Array, ...values: readonly Uint8Array[]): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key.slice().buffer as ArrayBuffer,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const input = concat(...values);
  return new Uint8Array(await crypto.subtle.sign(
    "HMAC",
    cryptoKey,
    input.slice().buffer as ArrayBuffer,
  ));
}

function equalConstantTime(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}

function messageHeader(message: ProtocolMessage, counter: number): Uint8Array {
  const output = new Uint8Array(8);
  const view = new DataView(output.buffer);
  output[0] = message.opcode;
  output[1] = message.flags;
  view.setUint16(2, message.requestId, true);
  view.setUint32(4, counter, true);
  return output;
}

export function encodeBase64Url(value: Uint8Array): string {
  let binary = "";
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function decodeBase64Url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("Invalid encoded access key.");
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function decodeAccessCode(value: string): Uint8Array {
  const compact = value.toUpperCase().replace(/[\s-]/g, "");
  if (!/^[A-Z2-7]{26}$/.test(compact)) {
    throw new Error("Enter the 26-character access key shown on the controller.");
  }
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  if ((alphabet.indexOf(compact.at(-1)!) & 0x03) !== 0) {
    throw new Error("The access key has an invalid final character.");
  }
  const output = new Uint8Array(ACCESS_KEY_BYTES);
  let accumulator = 0;
  let bits = 0;
  let outputIndex = 0;
  for (const symbol of compact) {
    accumulator = (accumulator << 5) | alphabet.indexOf(symbol);
    bits += 5;
    while (bits >= 8 && outputIndex < output.length) {
      bits -= 8;
      output[outputIndex] = (accumulator >>> bits) & 0xff;
      outputIndex += 1;
    }
  }
  if (outputIndex !== ACCESS_KEY_BYTES) throw new Error("Invalid access key length.");
  return output;
}

export class AccessCredentialStore {
  readonly #storage: Storage;

  constructor(storage = window.localStorage) {
    this.#storage = storage;
  }

  get(controllerId: string): Uint8Array | undefined {
    const encoded = this.#storage.getItem(STORAGE_PREFIX + controllerId.toUpperCase());
    if (!encoded) return undefined;
    try {
      const key = decodeBase64Url(encoded);
      return key.length === ACCESS_KEY_BYTES ? key : undefined;
    } catch {
      return undefined;
    }
  }

  set(controllerId: string, key: Uint8Array): void {
    if (!/^[0-9A-F]{16}$/i.test(controllerId) || key.length !== ACCESS_KEY_BYTES) {
      throw new Error("Invalid controller credential.");
    }
    this.#storage.setItem(STORAGE_PREFIX + controllerId.toUpperCase(), encodeBase64Url(key));
  }
}

export function captureEnrollmentFragment(
  location: Location = window.location,
  history: History = window.history,
  store = new AccessCredentialStore(),
): boolean {
  const match = /^#e9=([0-9a-f]{16})\.([A-Za-z0-9_-]+)$/i.exec(location.hash);
  if (!match) return false;
  const controllerId = match[1]!.toUpperCase();
  const key = decodeBase64Url(match[2]!);
  if (key.length !== ACCESS_KEY_BYTES) throw new Error("Invalid controller enrollment link.");
  store.set(controllerId, key);
  history.replaceState(history.state, "", `${location.pathname}${location.search}`);
  return true;
}

export class EnrollmentRequiredError extends Error {
  constructor(readonly controllerId: string) {
    super("Enter the access key shown on the controller to authorize this browser.");
    this.name = "EnrollmentRequiredError";
  }
}

export class InvalidAccessKeyError extends Error {
  constructor() {
    super("The access key was rejected. Check the controller and try again.");
    this.name = "InvalidAccessKeyError";
  }
}

export class ApplicationSession {
  readonly controllerId: string;
  readonly challenge: Uint8Array;
  readonly clientNonce: Uint8Array;
  readonly #sessionKey: Uint8Array;
  #sendCounter = 0;
  #receiveCounter = 0;
  #protectQueue: Promise<void> = Promise.resolve();
  #openQueue: Promise<void> = Promise.resolve();

  private constructor(
    controllerId: string,
    challenge: Uint8Array,
    clientNonce: Uint8Array,
    sessionKey: Uint8Array,
  ) {
    this.controllerId = controllerId;
    this.challenge = challenge;
    this.clientNonce = clientNonce;
    this.#sessionKey = sessionKey;
  }

  static async create(
    accessKey: Uint8Array,
    controllerId: string,
    challenge: Uint8Array,
  ): Promise<{ session: ApplicationSession; clientProof: Uint8Array }> {
    if (accessKey.length !== ACCESS_KEY_BYTES || !/^[0-9A-F]{16}$/.test(controllerId)
      || challenge.length !== 16) {
      throw new Error("Invalid authentication challenge.");
    }
    const clientNonce = crypto.getRandomValues(new Uint8Array(16));
    const transcript = [ascii(controllerId), challenge, clientNonce] as const;
    const clientProof = (await hmac(accessKey, ascii(CLIENT_PROOF_DOMAIN), ...transcript)).slice(0, AUTH_TAG_BYTES);
    const sessionKey = await hmac(accessKey, ascii(SESSION_KEY_DOMAIN), ...transcript);
    return {
      session: new ApplicationSession(controllerId, challenge.slice(), clientNonce, sessionKey),
      clientProof,
    };
  }

  async verifyServerProof(proof: Uint8Array): Promise<boolean> {
    const expected = (await hmac(
      this.#sessionKey,
      ascii(SERVER_PROOF_DOMAIN),
      this.challenge,
      this.clientNonce,
    )).slice(0, AUTH_TAG_BYTES);
    return equalConstantTime(expected, proof);
  }

  protect(message: ProtocolMessage): Promise<ProtocolMessage> {
    const run = (): Promise<ProtocolMessage> => this.#protectNow(message);
    const protectedMessage = this.#protectQueue.then(run, run);
    this.#protectQueue = protectedMessage.then(() => undefined, () => undefined);
    return protectedMessage;
  }

  async #protectNow(message: ProtocolMessage): Promise<ProtocolMessage> {
    if (message.flags !== FrameFlag.Request) throw new Error("Only requests may be protected for transmission.");
    const counter = this.#sendCounter + 1;
    if (counter > 0xffffffff) throw new Error("Authentication counter exhausted. Reconnect.");
    const tag = (await hmac(
      this.#sessionKey,
      ascii(MESSAGE_DOMAIN),
      this.challenge,
      this.clientNonce,
      messageHeader(message, counter),
      message.payload,
    )).slice(0, AUTH_TAG_BYTES);
    const trailer = new Uint8Array(AUTH_TRAILER_BYTES);
    new DataView(trailer.buffer).setUint32(0, counter, true);
    trailer.set(tag, 4);
    this.#sendCounter = counter;
    return { ...message, payload: concat(message.payload, trailer) };
  }

  open(message: ProtocolMessage): Promise<ProtocolMessage> {
    const run = (): Promise<ProtocolMessage> => this.#openNow(message);
    const openedMessage = this.#openQueue.then(run, run);
    this.#openQueue = openedMessage.then(() => undefined, () => undefined);
    return openedMessage;
  }

  async #openNow(message: ProtocolMessage): Promise<ProtocolMessage> {
    if (message.flags !== FrameFlag.Response && message.flags !== FrameFlag.Event) {
      throw new Error("Unexpected authenticated message kind.");
    }
    if (message.payload.length < AUTH_TRAILER_BYTES) throw new Error("Missing authentication trailer.");
    const logical = message.payload.slice(0, -AUTH_TRAILER_BYTES);
    const trailer = message.payload.slice(-AUTH_TRAILER_BYTES);
    const counter = new DataView(trailer.buffer, trailer.byteOffset, trailer.byteLength).getUint32(0, true);
    if (counter === 0 || counter <= this.#receiveCounter) throw new Error("Replayed controller message.");
    const expected = (await hmac(
      this.#sessionKey,
      ascii(MESSAGE_DOMAIN),
      this.challenge,
      this.clientNonce,
      messageHeader({ ...message, payload: logical }, counter),
      logical,
    )).slice(0, AUTH_TAG_BYTES);
    if (!equalConstantTime(expected, trailer.slice(4))) throw new Error("Controller message authentication failed.");
    this.#receiveCounter = counter;
    return { ...message, payload: logical };
  }
}
